import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, ShieldCheck } from "lucide-react";
import { staffUserService } from "@/services/staffUserService";
import ToastHelper from "@/utils/toastHelper";
import { getStaffIdentity } from "@/utils/staffAuth";
import { STAFF_ACCOUNT_TYPE } from "@/utils/staffAuth";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";

const emptyUser = {
  userName: "",
  email: "",
  accountTypeId: "",
  password: "",
  confirmPassword: "",
  isActive: true,
  isTestAccount: false,
};
const date = (value) =>
  value
    ? new Intl.DateTimeFormat("en-AU", { dateStyle: "medium" }).format(
        new Date(value),
      )
    : "-";

export default function StaffUsers() {
  const currentStaff = getStaffIdentity();
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [options, setOptions] = useState({ allowTestAccounts: false });
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [test, setTest] = useState("");
  const [dialog, setDialog] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [unlinkConfirm, setUnlinkConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      setRows(await staffUserService.list());
    } catch (e) {
      ToastHelper.apiError(e, "Unable to load staff users");
    }
  }, []);
  useEffect(() => {
    load();
    Promise.all([
      staffUserService.accountTypes(),
      staffUserService.options(),
      staffUserService.unlinkedTrainers(),
    ])
      .then(([t, o, p]) => {
        setTypes(t);
        setOptions(o);
        setTrainers(p);
      })
      .catch((e) => ToastHelper.apiError(e, "Unable to load staff options"));
  }, [load]);
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!search ||
            `${r.userName} ${r.email}`
              .toLowerCase()
              .includes(search.toLowerCase())) &&
          (!type || r.accountTypeId === Number(type)) &&
          (!status ||
            (status === "active") === (r.isActive && !r.isDisabled)) &&
          (!test || (test === "test") === r.isTestAccount),
      ),
    [rows, search, type, status, test],
  );
  const openAdd = () =>
    setDialog({
      kind: "user",
      mode: "add",
      item: { ...emptyUser, accountTypeId: types[0]?.accountTypeId || "" },
    });
  const openEdit = (item) =>
    setDialog({
      kind: "user",
      mode: "edit",
      item: { ...emptyUser, ...item, password: "", confirmPassword: "" },
    });
  const save = async () => {
    setSaving(true);
    try {
      dialog.mode === "add"
        ? await staffUserService.create(dialog.item)
        : await staffUserService.update(
            dialog.item.adminCredentialId,
            dialog.item,
          );
      ToastHelper.success(
        `Staff user ${dialog.mode === "add" ? "created" : "updated"} successfully`,
      );
      setDialog(null);
      await load();
    } catch (e) {
      ToastHelper.apiError(e, "Unable to save staff user");
    } finally {
      setSaving(false);
    }
  };
  const resetPassword = async () => {
    setSaving(true);
    try {
      await staffUserService.resetPassword(dialog.item.adminCredentialId, {
        newPassword: dialog.newPassword,
        confirmPassword: dialog.confirmPassword,
      });
      ToastHelper.success("Password reset successfully");
      setDialog(null);
    } catch (e) {
      ToastHelper.apiError(e, "Unable to reset password");
    } finally {
      setSaving(false);
    }
  };
  const changeStatus = async () => {
    try {
      await staffUserService.setEnabled(
        confirm.adminCredentialId,
        !(confirm.isActive && !confirm.isDisabled),
      );
      ToastHelper.success("Staff status updated");
      setConfirm(null);
      await load();
    } catch (e) {
      ToastHelper.apiError(e, "Unable to update staff status");
    }
  };
  const linkProfile = async () => {
    if (!dialog?.trainerId) return;
    setSaving(true);
    try {
      await staffUserService.linkTrainerCredential(Number(dialog.trainerId), dialog.item.adminCredentialId);
      ToastHelper.success("Trainer profile linked successfully");
      setDialog(null);
      await load();
      setTrainers(await staffUserService.unlinkedTrainers());
    } catch (e) {
      ToastHelper.apiError(e, "Unable to link trainer profile");
    } finally {
      setSaving(false);
    }
  };
  const unlinkProfile = async (item) => {
    if (!item.trainerId) return;
    try {
      await staffUserService.unlinkTrainerCredential(item.trainerId);
      ToastHelper.success("Trainer profile unlinked successfully");
      setUnlinkConfirm(null);
      await load();
      setTrainers(await staffUserService.unlinkedTrainers());
    } catch (e) {
      ToastHelper.apiError(e, "Unable to unlink trainer profile");
    }
  };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">
            Staff Users
          </h1>
          <p className="text-sm text-slate-500">
            Manage staff portal credentials and access.
          </p>
        </div>
        <button
          className="wc-btn-primary inline-flex items-center gap-2"
          onClick={openAdd}
        >
          <Plus className="h-4 w-4" /> Add Staff User
        </button>
      </div>
      <div className="wc-card bg-white p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <label className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <input
              className="wc-input pl-9"
              placeholder="Search staff..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <select
            className="wc-input"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">All account types</option>
            {types.map((x) => (
              <option key={x.accountTypeId} value={x.accountTypeId}>
                {x.accountTypeName}
              </option>
            ))}
          </select>
          <select
            className="wc-input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
              <option value="disabled">Inactive</option>
          </select>
          <select
            className="wc-input"
            value={test}
            onChange={(e) => setTest(e.target.value)}
          >
            <option value="">All accounts</option>
            <option value="real">Real accounts</option>
            <option value="test">Test accounts</option>
          </select>
        </div>
      </div>
      <div className="wc-card overflow-x-auto bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              {[
                "User Name",
                "Email",
                "Account Type",
                "Trainer Profile",
                "Rank",
                "Status",
                "Test Account",
                "Last Login",
                "Actions",
              ].map((x) => (
                <th key={x} className="px-4 py-3">
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const manageable =
                currentStaff.rankNo > r.rankNo &&
                currentStaff.userId !== r.adminCredentialId;
              return (
                <tr
                  key={r.adminCredentialId}
                  className="border-b last:border-0"
                >
                  <td className="px-4 py-3 font-semibold text-[#0A2540]">
                    {r.userName}
                  </td>
                  <td className="px-4 py-3">{r.email}</td>
                  <td className="px-4 py-3">{r.accountTypeName}</td>
                  <td className="px-4 py-3">
                    {r.accountTypeId === STAFF_ACCOUNT_TYPE.TRAINER ? (
                      r.trainerId ? (
                        <Link
                          className="font-semibold text-blue-700"
                          to={`/admin/trainers?trainerId=${r.trainerId}`}
                        >
                          {r.trainerName}
                        </Link>
                      ) : (
                        <span className="text-amber-700">Not Linked</span>
                      )
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-4 py-3">{r.rankNo}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`wc-badge ${r.isActive && !r.isDisabled ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
                    >
                      {r.isDeleted ? "Deleted" : r.isActive && !r.isDisabled ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {r.isTestAccount ? "Yes" : "No"}
                  </td>
                  <td className="px-4 py-3">{date(r.lastLoginAt)}</td>
                  <td className="px-4 py-3">
                    {manageable ? (
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="text-sm font-semibold text-blue-700"
                          onClick={() => openEdit(r)}
                        >
                          Edit
                        </button>
                        <button
                          className="text-sm font-semibold text-blue-700"
                          onClick={() =>
                            setDialog({
                              kind: "password",
                              item: r,
                              newPassword: "",
                              confirmPassword: "",
                            })
                          }
                        >
                          Reset Password
                        </button>
                        <button
                          className={`text-sm font-semibold ${r.isActive && !r.isDisabled ? "text-rose-700" : "text-emerald-700"}`}
                          onClick={() => setConfirm(r)}
                        >
                          {r.isActive && !r.isDisabled ? "Set inactive" : "Set active"}
                        </button>
                        {r.accountTypeId === STAFF_ACCOUNT_TYPE.TRAINER && (
                          <button
                            className="text-sm font-semibold text-blue-700"
                            onClick={() => r.trainerId ? setUnlinkConfirm(r) : setDialog({ kind: "profile", item: r, trainerId: "" })}
                          >
                            {r.trainerId ? "Unlink Profile" : "Link Profile"}
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Protected</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500">
            No staff users found.
          </div>
        )}
      </div>
      {dialog?.kind === "user" && (
        <UserDialog
          dialog={dialog}
          setDialog={setDialog}
          types={types}
          trainers={trainers}
          options={options}
          saving={saving}
          save={save}
        />
      )}
      {dialog?.kind === "password" && (
        <PasswordDialog
          dialog={dialog}
          setDialog={setDialog}
          saving={saving}
          save={resetPassword}
        />
      )}
      {dialog?.kind === "profile" && (
        <LinkProfileDialog dialog={dialog} setDialog={setDialog} trainers={trainers} saving={saving} save={linkProfile} />
      )}
      {confirm && (
        <ConfirmDialog
          item={confirm}
          close={() => setConfirm(null)}
          proceed={changeStatus}
        />
      )}
      {unlinkConfirm && (
        <UnlinkProfileConfirmDialog item={unlinkConfirm} close={() => setUnlinkConfirm(null)} proceed={() => unlinkProfile(unlinkConfirm)} />
      )}
    </div>
  );
}

function LinkProfileDialog({ dialog, setDialog, trainers, saving, save }) {
  return (
    <Modal title={`Link Trainer Profile — ${dialog.item.userName}`} close={() => setDialog(null)} action={save} actionText="Link Profile" saving={saving}>
      <label>
        <span className="wc-label">Trainer Profile *</span>
        <select className="wc-input" value={dialog.trainerId} onChange={(event) => setDialog((current) => ({ ...current, trainerId: event.target.value }))}>
          <option value="">Select trainer profile</option>
          {trainers.map((trainer) => <option key={trainer.trainerId} value={trainer.trainerId}>{trainer.firstName} {trainer.lastName} · {trainer.email}</option>)}
        </select>
      </label>
    </Modal>
  );
}

function Modal({ title, children, close, action, actionText, saving }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
      <div className="w-full max-w-xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-extrabold text-[#0A2540]">{title}</h2>
          <button onClick={close} className="text-2xl">
            ×
          </button>
        </div>
        <div className="p-6">{children}</div>
        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <button className="wc-btn-outline" onClick={close}>
            Cancel
          </button>
          <button className="wc-btn-primary" disabled={saving} onClick={action}>
            {saving ? "Saving..." : actionText}
          </button>
        </div>
      </div>
    </div>
  );
}
function UserDialog({
  dialog,
  setDialog,
  types,
  trainers,
  options,
  saving,
  save,
}) {
  const change = (key, value) =>
    setDialog((d) => ({ ...d, item: { ...d.item, [key]: value } }));
  return (
    <Modal
      title={dialog.mode === "add" ? "Add Staff User" : "Edit Staff User"}
      close={() => setDialog(null)}
      action={save}
      actionText={dialog.mode === "add" ? "Create User" : "Save Changes"}
      saving={saving}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {[
          ["userName", "User Name", "text"],
          ["email", "Email", "email"],
        ].map(([k, l, t]) => (
          <label key={k}>
            <span className="wc-label">{l} *</span>
            <input
              className="wc-input"
              type={t}
              value={dialog.item[k]}
              onChange={(e) => change(k, e.target.value)}
            />
          </label>
        ))}
        <label className="sm:col-span-2">
          <span className="wc-label">Account Type *</span>
          <select
            className="wc-input"
            value={dialog.item.accountTypeId}
            onChange={(e) => change("accountTypeId", Number(e.target.value))}
          >
            <option value="">Select account type</option>
            {types.map((x) => (
              <option key={x.accountTypeId} value={x.accountTypeId}>
                {x.accountTypeName}
              </option>
            ))}
          </select>
        </label>
        {dialog.mode === "add" &&
          Number(dialog.item.accountTypeId) === STAFF_ACCOUNT_TYPE.TRAINER && (
            <label className="sm:col-span-2">
              <span className="wc-label">Trainer Profile *</span>
              <select
                className="wc-input"
                value={dialog.item.trainerProfileId || ""}
                onChange={(e) =>
                  change("trainerProfileId", Number(e.target.value))
                }
              >
                <option value="">Select trainer profile</option>
                {trainers.map((x) => (
                  <option key={x.trainerId} value={x.trainerId}>
                    {x.firstName} {x.lastName}
                  </option>
                ))}
              </select>
            </label>
          )}
        {dialog.mode === "add" &&
          [
            ["password", "Password"],
            ["confirmPassword", "Confirm Password"],
          ].map(([k, l]) => (
            <label key={k}>
              <span className="wc-label">{l} *</span>
              <input
                className="wc-input"
                type="password"
                autoComplete="new-password"
                value={dialog.item[k]}
                onChange={(e) => change(k, e.target.value)}
              />
            </label>
          ))}
        <label className="flex items-center gap-3">
          <Switch
            checked={dialog.item.isActive}
            className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-rose-400"
            onCheckedChange={(checked) => change("isActive", checked)}
          />
          <span className="text-sm font-semibold">
            {dialog.item.isActive ? "Active" : "Inactive"}
          </span>
        </label>
        {options.allowTestAccounts && (
          <label className="flex items-center gap-3">
            <Switch
              checked={dialog.item.isTestAccount}
              onCheckedChange={(checked) => change("isTestAccount", checked)}
            />
            <span className="text-sm font-semibold">Test Account</span>
          </label>
        )}
      </div>
    </Modal>
  );
}
function PasswordDialog({ dialog, setDialog, saving, save }) {
  const change = (key, value) => setDialog((d) => ({ ...d, [key]: value }));
  return (
    <Modal
      title={`Reset Password — ${dialog.item.userName}`}
      close={() => setDialog(null)}
      action={save}
      actionText="Reset Password"
      saving={saving}
    >
      <div className="space-y-4">
        {[
          ["newPassword", "New Password"],
          ["confirmPassword", "Confirm Password"],
        ].map(([k, l]) => (
          <label key={k}>
            <span className="wc-label">{l}</span>
            <input
              className="wc-input"
              type="password"
              autoComplete="new-password"
              value={dialog[k]}
              onChange={(e) => change(k, e.target.value)}
            />
          </label>
        ))}
      </div>
    </Modal>
  );
}
function ConfirmDialog({ item, close, proceed }) {
  const disabling = item.isActive && !item.isDisabled;
  return (
    <Modal
      title={`${disabling ? "Set inactive" : "Set active"} staff user?`}
      close={close}
      action={proceed}
      actionText={`${disabling ? "Set inactive" : "Set active"} User`}
    >
      <div className="flex gap-3">
        <ShieldCheck className="h-6 w-6 text-orange-600" />
        <p className="text-sm text-slate-600">
          <strong>{item.userName}</strong>{" "}
          {disabling
            ? "will no longer be able to sign in."
            : "will be able to sign in again."}
        </p>
      </div>
    </Modal>
  );
}

function UnlinkProfileConfirmDialog({ item, close, proceed }) {
  return (
    <Modal title="Unlink trainer profile?" close={close} action={proceed} actionText="Unlink Profile">
      <div className="flex gap-3">
        <ShieldCheck className="h-6 w-6 shrink-0 text-orange-600" />
        <p className="text-sm text-slate-600">
          The trainer profile <strong>{item.trainerName}</strong> will be unlinked from <strong>{item.userName}</strong>. The staff account and trainer profile will remain available.
        </p>
      </div>
    </Modal>
  );
}
